/* eslint
  "styled-components-a11y/label-has-associated-control": [
    "error", { "controlComponents": ["UploadAttachmentInput"] },
  ],
*/

import { Box, styled, Typography, useTheme } from '@mui/material';
import type React from 'react';
import { useRef, useState } from 'react';
import { IconPaperclip } from '@tabler/icons-react';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  addScreenReaderNotification,
  selectConversationId,
  setDismissableNotification,
} from 'reducers/workflows';
import { getPresignedUrlToS3 } from 'actions/workflows/workflows';
import { postFileToS3 } from 'api/requests';
import { Attachment } from './Attachment';
import { addLiveChatEventMessage, sendUserReply } from 'actions/live-chat';
import {
  selectLiveChatIntegration,
  selectIsSuncoLiveChatActive,
  selectChatId,
  selectIsInAssistantMode,
} from 'reducers/live-chat';
import type { FileAttachment } from 'api/types';
import { applyZopimFunction } from 'embed/zopim';
import { applyKustomerCoreFunctionWithCallback } from 'embed/kustomerCore';
import { PopupModal } from 'components/PopupModal';
import { OrgThemedButton } from 'components/OrgThemedButton';
import { selectWidgetInitMethod } from 'reducers/config';
import { sendErrorToSentry } from 'utils/sendErrorToSentry';
import prettyBytes from 'pretty-bytes';
import { sendFile } from 'components/salesforce-messaging-one-chat/messagingService';
import { v4 as uuidv4 } from 'uuid';
import { getErrorMessage } from 'utils/utils';

export const UploadAttachmentButton = ({
  isDisabled = false,
  maxFileSize,
}: {
  isDisabled?: boolean;
  maxFileSize?: number;
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const liveChatIntegration = useAppSelector(selectLiveChatIntegration);
  const isSuncoLiveChatActive = useAppSelector(selectIsSuncoLiveChatActive);
  const chatId = useAppSelector(selectChatId);
  const isInAssistantMode = useAppSelector(selectIsInAssistantMode);
  const widgetInitMethod = useAppSelector(selectWidgetInitMethod);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const conversationId = useAppSelector(selectConversationId);

  const [isSendFileModalOpen, setIsSendFileModalOpen] = useState(false);
  const [files, setFiles] = useState<Array<File>>([]);
  const [isSendingFiles, setIsSendingFiles] = useState(false);

  const sendAttachments = async (attachments: FileAttachment[]) => {
    switch (liveChatIntegration) {
      case 'zendesk':
        files.forEach(file => {
          applyZopimFunction(widgetInitMethod, 'sendFile', file);
        });
        break;
      case 'kustomer':
        return applyKustomerCoreFunctionWithCallback('sendMessage', {
          attachments: files.map(file => ({ file })),
          conversationId: chatId,
          isInAssistantMode,
        })
          .then(() => {
            dispatch(sendUserReply({ attachments, message: '' }));
          })
          .catch((err: unknown) => {
            const errorMessage = 'Failed to send attachments';
            dispatch(addScreenReaderNotification(errorMessage));
            dispatch(addLiveChatEventMessage({ message: errorMessage }));
            sendErrorToSentry({
              additionalContext: { attachments, chatId },
              conversationId: conversationId,
              error: `Kustomer One Chat failed to send attachments -- ${err}`,
            });
          });
      case 'salesforce':
        const promisedAttachments: Promise<FileAttachment>[] = files.map(
          async (file: File): Promise<FileAttachment> => {
            await sendFile(chatId, file, uuidv4(), uuidv4());

            return {
              content_type: file.type,
              file_extension: file.name.split('.').pop(),
              filesize: file.size,
              name: file.name,
              url: '',
            };
          },
        );

        try {
          await Promise.all(promisedAttachments);
          dispatch(sendUserReply({ attachments, message: '' }));
        } catch (err: unknown) {
          const errorMessage = 'Failed to send attachments';
          dispatch(addScreenReaderNotification(errorMessage));
          dispatch(addLiveChatEventMessage({ message: errorMessage }));
          sendErrorToSentry({
            additionalContext: { chatId },
            conversationId: conversationId,
            error: `Salesforce One Chat failed to send attachments -- ${getErrorMessage(err)}`,
          });
        }
        break;
      default:
        dispatch(sendUserReply({ attachments, message: '' }));
        break;
    }
  };

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputFiles = fileInputRef.current?.files || [];

    // validate file size
    if (maxFileSize) {
      for (const file of Array.from(inputFiles)) {
        if (file.size > maxFileSize) {
          dispatch(
            setDismissableNotification(
              `Maximum file size is ${prettyBytes(maxFileSize)}.`,
            ),
          );
          setFiles([]);
          e.target.value = '';
          return;
        }
      }
    }

    if (inputFiles.length > 0) {
      setIsSendFileModalOpen(true);
      setFiles(Array.from(inputFiles));
    }

    e.target.value = '';
  };

  const onSendFiles = async () => {
    setIsSendingFiles(true);

    const promisedAttachments: Promise<FileAttachment>[] = files.map(
      async (file: File): Promise<FileAttachment> => {
        const { downloadUrl, type, url } = await getPresignedUrlToS3(
          conversationId,
          file.name,
          file.size,
          true,
        );

        await postFileToS3(url, type, file);

        return {
          content_type: file.type,
          file_extension: file.name.split('.').pop(),
          filesize: file.size,
          name: file.name,
          url: downloadUrl || '',
        };
      },
    );

    const attachments = await Promise.all(promisedAttachments);

    await sendAttachments(attachments);
    dispatch(addScreenReaderNotification('Files uploaded'));
    setIsSendFileModalOpen(false);
    setFiles([]);
    setIsSendingFiles(false);
  };

  const handleSuncoAttachmentOnClick = () => {
    const zendeskIFrame = document.querySelector<HTMLObjectElement>(
      '#web-messenger-container',
    );

    if (zendeskIFrame) {
      const zendeskWidgetDoc = zendeskIFrame.contentDocument;
      if (zendeskWidgetDoc) {
        const uploadElements =
          zendeskWidgetDoc.querySelectorAll<HTMLElement>('.upload-icon');
        uploadElements[0]?.click();

        // Decide which to grab (0 - Location, 1 - File, 2 - Image)
        const fileUploadElements =
          zendeskWidgetDoc.querySelectorAll<HTMLElement>('.upload-menu-item');
        fileUploadElements[1]?.click();
      }
    }
  };

  const onModalClose = () => {
    setIsSendFileModalOpen(false);
    setFiles([]);
  };

  if (liveChatIntegration === 'sunco' && isSuncoLiveChatActive) {
    return (
      <StyledButton
        aria-label='Upload file'
        disabled={isDisabled}
        onClick={handleSuncoAttachmentOnClick}
        type='button'
      >
        <IconPaperclip size={24} />
      </StyledButton>
    );
  }

  return (
    <>
      {/* hidden file input */}
      <StyledLabel>
        <Box sx={{ cursor: 'pointer', height: '20px' }}>
          <IconPaperclip
            color={
              isDisabled
                ? theme.palette.colors.slate[100]
                : theme.palette.colors.black
            }
            size={24}
            style={{ transform: 'rotate(315deg)' }}
          />
        </Box>
        <input
          aria-label='file input'
          className='visually-hidden'
          disabled={isDisabled}
          multiple={true}
          onChange={onChangeHandler}
          ref={fileInputRef}
          type='file'
        />
      </StyledLabel>
      {/* send file modal */}
      <PopupModal
        onClickAway={onModalClose}
        open={isSendFileModalOpen}
        position='bottom'
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}
        >
          <Typography color={theme.palette.colors.black} variant='font20'>
            Send file
          </Typography>
          <Box display='flex' flexDirection='column' gap='12px' width='100%'>
            {files.map((file, index) => (
              <Attachment
                attachment={{
                  content_type: file.type,
                  filesize: file.size,
                  name: file.name,
                  url: URL.createObjectURL(file),
                }}
                key={file.name}
                onTrashIconClicked={() => {
                  if (files.length === 1) {
                    setIsSendFileModalOpen(false);
                    setFiles([]);
                  }
                  const newFiles = files.filter(
                    (_, currentIndex) => currentIndex !== index,
                  );
                  setFiles(newFiles);
                }}
                showTrashIcon
              />
            ))}
          </Box>
          <Box display='flex' gap={1} width='100%'>
            <OrgThemedButton
              color='error'
              fullWidth
              onClick={onModalClose}
              variant='outlined'
            >
              Cancel
            </OrgThemedButton>
            <OrgThemedButton
              disabled={isSendingFiles}
              fullWidth
              onClick={onSendFiles}
              variant='contained'
            >
              Send file
            </OrgThemedButton>
          </Box>
        </Box>
      </PopupModal>
    </>
  );
};

const StyledButton = styled('button')`
  cursor: pointer;
  outline: none;
  background-color: ${props => props.theme.palette.colors.white};
  height: 20px;
  width: 20px;
  padding: 0;
  border: none;
`;

const StyledLabel = styled('label')`
  :focus-within {
    outline: 2px solid var(--legible-element-color);
  }
`;
